import { Injectable, computed, signal } from '@angular/core';
import {
  VacationProperty,
  WizardMode,
  WizardStep,
  PropertyBasicInfo,
  UnitType,
  PricingRules,
  Season
} from '../models';
import { WIZARD_LIMITS } from '../config/wizard-limits';

@Injectable()
export class PropertyWizardStateService {
  private readonly _currentStep = signal<number>(0);
  private readonly _mode = signal<WizardMode>('create');
  private readonly _basicInfo = signal<PropertyBasicInfo | null>(null);
  private readonly _unitTypes = signal<UnitType[]>([]);
  private readonly _pricingRules = signal<PricingRules | null>(null);
  private readonly _seasons = signal<Season[]>([]);
  private readonly _stepValidity = signal<boolean[]>([false, false, false]);

  readonly currentStep = this._currentStep.asReadonly();
  readonly mode = this._mode.asReadonly();
  readonly basicInfo = this._basicInfo.asReadonly();
  readonly unitTypes = this._unitTypes.asReadonly();
  readonly pricingRules = this._pricingRules.asReadonly();
  readonly seasons = this._seasons.asReadonly();

  readonly isViewMode = computed(() => this._mode() === 'view');
  readonly isEditMode = computed(() => this._mode() === 'edit');
  readonly isCreateMode = computed(() => this._mode() === 'create');

  readonly steps = computed<WizardStep[]>(() => {
    const validity = this._stepValidity();
    return [
      { index: 0, label: 'objects.wizard.steps.basicInfo', isValid: validity[0], isCompleted: this._currentStep() > 0 },
      { index: 1, label: 'objects.wizard.steps.unitTypes', isValid: validity[1], isCompleted: this._currentStep() > 1 },
      { index: 2, label: 'objects.wizard.steps.pricingRules', isValid: validity[2], isCompleted: this._currentStep() > 2 }
    ];
  });

  readonly canGoNext = computed(() => {
    const validity = this._stepValidity();
    const step = this._currentStep();
    return validity[step] && step < 2;
  });

  readonly canGoBack = computed(() => this._currentStep() > 0);

  /** Total number of images across all steps (property + all unit types) */
  readonly totalImages = computed(() => {
    const propertyImages = this._basicInfo()?.images?.length || 0;
    const unitImages = this._unitTypes().reduce((sum, ut) => sum + (ut.images?.length || 0), 0);
    return propertyImages + unitImages;
  });

  /** Remaining images available before hitting the global limit */
  readonly remainingImages = computed(() =>
    Math.max(0, WIZARD_LIMITS.GLOBAL_MAX_IMAGES - this.totalImages())
  );

  /** Whether the global image limit has been reached */
  readonly isGlobalImageLimitReached = computed(() =>
    this.totalImages() >= WIZARD_LIMITS.GLOBAL_MAX_IMAGES
  );

  readonly canSubmit = computed(() => {
    const validity = this._stepValidity();
    return validity.every(v => v) && this._currentStep() === 2;
  });

  readonly propertyData = computed<VacationProperty | null>(() => {
    const basicInfo = this._basicInfo();
    const unitTypes = this._unitTypes();
    const pricingRules = this._pricingRules();
    const seasons = this._seasons();

    if (!basicInfo || unitTypes.length === 0 || !pricingRules) {
      return null;
    }

    return {
      basicInfo,
      unitTypes,
      pricingRules,
      seasons
    };
  });

  initialize(mode: WizardMode, initialData?: VacationProperty): void {
    this._mode.set(mode);
    this._currentStep.set(0);

    if (initialData) {
      this._basicInfo.set(initialData.basicInfo);
      this._unitTypes.set(initialData.unitTypes);
      this._pricingRules.set(initialData.pricingRules);
      this._seasons.set(initialData.seasons);
    } else {
      this._basicInfo.set(null);
      this._unitTypes.set([]);
      this._pricingRules.set(this.getDefaultPricingRules());
      this._seasons.set([]);
    }
  }

  /**
   * Initialize with only basic info (lazy loading mode).
   * Types and pricing will be loaded on demand when user navigates to those steps.
   */
  initializeBasic(mode: WizardMode, basicInfo: PropertyBasicInfo): void {
    this._mode.set(mode);
    this._currentStep.set(0);
    this._basicInfo.set(basicInfo);
    this._unitTypes.set([]);
    this._pricingRules.set(this.getDefaultPricingRules());
    this._seasons.set([]);
  }

  /**
   * Set unit types data (loaded lazily for step 2)
   */
  setTypesData(unitTypes: UnitType[]): void {
    this._unitTypes.set(unitTypes);
  }

  /**
   * Set pricing data (loaded lazily for step 3)
   */
  setPricingData(pricingRules: PricingRules, seasons: Season[]): void {
    this._pricingRules.set(pricingRules);
    this._seasons.set(seasons);
  }

  setStepValidity(stepIndex: number, isValid: boolean): void {
    this._stepValidity.update(validity => {
      const newValidity = [...validity];
      newValidity[stepIndex] = isValid;
      return newValidity;
    });
  }

  goToStep(step: number): void {
    if (step >= 0 && step <= 2) {
      this._currentStep.set(step);
    }
  }

  nextStep(): void {
    if (this.canGoNext()) {
      this._currentStep.update(s => s + 1);
    }
  }

  previousStep(): void {
    if (this.canGoBack()) {
      this._currentStep.update(s => s - 1);
    }
  }

  updateBasicInfo(info: PropertyBasicInfo): void {
    this._basicInfo.set(info);
  }

  updateUnitTypes(types: UnitType[]): void {
    this._unitTypes.set(types);
  }

  addUnitType(unitType: UnitType): void {
    this._unitTypes.update(types => [...types, unitType]);
  }

  updateUnitType(unitType: UnitType): void {
    this._unitTypes.update(types =>
      types.map(t => t.id === unitType.id ? unitType : t)
    );
  }

  removeUnitType(unitTypeId: string): void {
    this._unitTypes.update(types => types.filter(t => t.id !== unitTypeId));
    this._seasons.update(seasons =>
      seasons.map(s => ({
        ...s,
        unitPrices: s.unitPrices.filter(up => up.unitTypeId !== unitTypeId)
      }))
    );
  }

  updatePricingRules(rules: PricingRules): void {
    this._pricingRules.set(rules);
  }

  updateSeasons(seasons: Season[]): void {
    this._seasons.set(seasons);
  }

  addSeason(season: Season): void {
    this._seasons.update(seasons => [...seasons, season]);
  }

  updateSeason(season: Season): void {
    this._seasons.update(seasons =>
      seasons.map(s => s.id === season.id ? season : s)
    );
  }

  removeSeason(seasonId: string): void {
    this._seasons.update(seasons => seasons.filter(s => s.id !== seasonId));
  }

  private getDefaultPricingRules(): PricingRules {
    return {
      electricityPolicy: 'included',
      petPolicy: 'not_allowed',
      depositPercentage: 30,
      checkInTime: '15:00',
      checkOutTime: '10:00',
      minStay: 1,
      depositPaymentDays: 3
    };
  }

  reset(): void {
    this._currentStep.set(0);
    this._mode.set('create');
    this._basicInfo.set(null);
    this._unitTypes.set([]);
    this._pricingRules.set(this.getDefaultPricingRules());
    this._seasons.set([]);
    this._stepValidity.set([false, false, false]);
  }

}
